import {
  Alert,
  Button,
  Divider,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import {
  CLINICIAN_LABEL_FIELDS,
  ClinicianLabelDraftMap,
  buildClinicianReviewPayload,
  draftsFromLabels,
  isClinicianLabelDraftComplete,
} from "@utils/clinicianLabelUtils";
import {
  ClinicianLabelField,
  ClinicianLabels,
  ClinicianReviewPayload,
  TestCommentEntry,
} from "types/tests";
import { useEffect, useMemo, useState } from "react";

import { CLINICIAN_COMMENT_MAX_LENGTH } from "@constants/clinicianLabelConstants";
import ClinicianLabelFields from "./ClinicianLabelFields";
import ClinicianReviewHistory from "./ClinicianReviewHistory";
import { useTranslation } from "react-i18next";

interface ClinicianReviewDialogProps {
  open: boolean;
  onClose: () => void;
  comment: string;
  labels?: ClinicianLabels;
  commentHistory?: TestCommentEntry[];
  saving?: boolean;
  onSave: (payload: ClinicianReviewPayload) => Promise<void>;
}

export default function ClinicianReviewDialog({
  open,
  onClose,
  comment,
  labels,
  commentHistory,
  saving,
  onSave,
}: ClinicianReviewDialogProps) {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<ClinicianLabelDraftMap>(() => draftsFromLabels(labels));
  const [commentDraft, setCommentDraft] = useState(comment ?? "");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDrafts(draftsFromLabels(labels));
    setCommentDraft(comment ?? "");
    setFailed(false);
  }, [open, labels, comment]);

  const payload = useMemo(
    () =>
      buildClinicianReviewPayload(drafts, labels, {
        next: commentDraft,
        current: comment ?? "",
      }),
    [drafts, labels, commentDraft, comment],
  );
  const incomplete = CLINICIAN_LABEL_FIELDS.some(
    (field) => !isClinicianLabelDraftComplete(drafts[field]),
  );

  function handleChange(field: ClinicianLabelField, draft: ClinicianLabelDraftMap[typeof field]) {
    setDrafts((current) => ({ ...current, [field]: draft }));
  }

  async function handleSave() {
    if (!payload) return;
    setFailed(false);
    try {
      await onSave(payload);
      onClose();
    } catch {
      setFailed(true);
    }
  }

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={t("ResultList.comment")}
      size="xl"
      lockScroll={false}
      centered
    >
      <Stack gap="md">
        <Group align="flex-end" gap="md" wrap="wrap">
          {CLINICIAN_LABEL_FIELDS.map((field) => (
            <ClinicianLabelFields
              key={field}
              field={field}
              draft={drafts[field]}
              onChange={handleChange}
              disabled={saving}
              flex="1 1 200px"
            />
          ))}
        </Group>

        <Textarea
          label={t("ClinicianLabel.commentLabel")}
          value={commentDraft}
          minRows={3}
          autosize
          maxLength={CLINICIAN_COMMENT_MAX_LENGTH}
          disabled={saving}
          onChange={(event) => setCommentDraft(event.currentTarget.value)}
          data-testid="clinician-review-comment"
        />

        {failed && (
          <Alert color="red" data-testid="clinician-review-error">
            {t("ClinicianLabel.saveFailed")}
          </Alert>
        )}

        <Divider />

        <Text fw={600}>{t("ClinicianLabel.historyTitle")}</Text>
        <ScrollArea.Autosize mah={280}>
          <ClinicianReviewHistory labels={labels} commentHistory={commentHistory} />
        </ScrollArea.Autosize>
      </Stack>

      <Group justify="flex-end" mt="md">
        <Button variant="outline" onClick={onClose}>
          {t("ResultList.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={!payload || incomplete}
          data-testid="clinician-review-dialog-save"
        >
          {t("ResultList.saveCommentChanges")}
        </Button>
      </Group>
    </Modal>
  );
}
